import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import '../styles/admin.css';
import { DashIcon, CatIcon, ProdIcon, ProjIcon, EventIcon, TeamIcon, SettingsIcon, ServiceIcon, MessageIcon, SupplierIcon, ReferenceIcon, ChevronLeft, ChevronRight, CalendarIcon, ShopIcon, SlideIcon } from '../components/icons.jsx';
import { useAuth } from '../state/useAuth';
import { RefreshCcw, ShieldAlert, User, LayoutDashboard, Globe } from 'lucide-react';

export default function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { role } = useAuth();

  const handleRepair = async () => {
    if (!window.confirm("Voulez-vous réinitialiser le cache et les Service Workers de l'application ? Cela peut régler les problèmes d'upload.")) return;
    
    // 1. Unregister all SW
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (let r of registrations) await r.unregister();
    }
    
    // 2. Clear Caches
    if ('caches' in window) {
      const names = await caches.keys();
      for (let n of names) await caches.delete(n);
    }
    
    alert("Nettoyage effectué. La page va se recharger.");
    window.location.reload(true);
  };

  useEffect(() => {
    const v = localStorage.getItem('admin_sidebar_collapsed');
    if (v === '1') setCollapsed(true);
  }, []);

  useEffect(() => {
    function handleResize() {
      const mobile = window.innerWidth <= 900;
      setIsMobile(mobile);
      if (mobile) setMobileOpen(true); else setMobileOpen(false);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) localStorage.setItem('admin_sidebar_collapsed', collapsed ? '1' : '0');
  }, [collapsed, isMobile]);

  const handleToggleSidebar = () => { if (isMobile) setMobileOpen(v => !v); else setCollapsed(v => !v); };
  const handleNavClick = () => { if (isMobile) setMobileOpen(false); };
  const effectiveCollapsed = isMobile ? !mobileOpen : collapsed;

  return (
    <div className={"admin-wrap" + (effectiveCollapsed ? ' is-collapsed' : '') + (mobileOpen ? ' is-mobile-open' : '')}>
      {isMobile && mobileOpen && <div className="admin-overlay" onClick={() => setMobileOpen(false)} />}
      <aside className="admin-aside">
        <div className="admin-brand">
          <img src="/logo192.png" alt="AEF" style={{ height: 28 }} />
          <div className="admin-brand-label" style={{ fontWeight: 800 }}>AEF Admin</div>
          <button className="admin-toggle" onClick={handleToggleSidebar}>
            {effectiveCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>
        <nav className="admin-nav">
          <div className="nav-section">Logistique</div>
          {role === 'admin' && <NavLink to="/admin" end onClick={handleNavClick}><span className="ico"><DashIcon /></span><span className="label">Dashboard</span></NavLink>}
          <NavLink to="/admin/planning" onClick={handleNavClick}><span className="ico"><CalendarIcon /></span><span className="label">Planning</span></NavLink>
          <NavLink to="/admin/chantiers" onClick={handleNavClick}><span className="ico"><ShopIcon /></span><span className="label">Chantiers</span></NavLink>
          
          <div className="nav-section">Équipe & Social</div>
          {role === 'admin' && <NavLink to="/admin/team" onClick={handleNavClick}><span className="ico"><TeamIcon /></span><span className="label">Équipe</span></NavLink>}
          {role === 'admin' && <NavLink to="/admin/users" onClick={handleNavClick}><span className="ico"><User size={18} /></span><span className="label">Utilisateurs</span></NavLink>}
          <NavLink to="/admin/messages" onClick={handleNavClick}><span className="ico"><MessageIcon /></span><span className="label">Messages</span></NavLink>

          <div className="nav-section">Contenu Site</div>
          <NavLink to="/admin/slides" onClick={handleNavClick}><span className="ico"><SlideIcon /></span><span className="label">Bandeaux Accueil</span></NavLink>
          <NavLink to="/admin/categories" onClick={handleNavClick}><span className="ico"><CatIcon /></span><span className="label">Catégories</span></NavLink>
          <NavLink to="/admin/products" onClick={handleNavClick}><span className="ico"><ProdIcon /></span><span className="label">Produits</span></NavLink>
          <NavLink to="/admin/projects" onClick={handleNavClick}><span className="ico"><ProjIcon /></span><span className="label">Réalisations</span></NavLink>
          <NavLink to="/admin/events" onClick={handleNavClick}><span className="ico"><EventIcon /></span><span className="label">Événements</span></NavLink>

          <div className="nav-section">Système</div>
          {role === 'admin' && (
            <>
              <NavLink to="/admin/settings" onClick={handleNavClick}><span className="ico"><SettingsIcon /></span><span className="label">Paramètres</span></NavLink>
              <NavLink to="/admin/services" onClick={handleNavClick}><span className="ico"><ServiceIcon /></span><span className="label">Services</span></NavLink>
              <NavLink to="/admin/suppliers" onClick={handleNavClick}><span className="ico"><SupplierIcon /></span><span className="label">Fournisseurs</span></NavLink>
              <NavLink to="/admin/references" onClick={handleNavClick}><span className="ico"><ReferenceIcon /></span><span className="label">Références</span></NavLink>
            </>
          )}
        </nav>
      </aside>
      <section className="admin-main">
        <div className="admin-topbar">
          <div className="admin-topbar-title">
            <LayoutDashboard size={20} color="var(--admin-primary)" />
            <span className="hide-sm" style={{ marginLeft: 10 }}>AEF Administration</span>
          </div>
          <div className="admin-topbar-btns">
            <a href="/" target="_blank" className="btn ghost" title="Voir le site">
              <Globe size={20} />
              <span className="hide-sm" style={{ marginLeft: 8 }}>Voir le site</span>
            </a>
          </div>
        </div>
        <Outlet />
      </section>

      {/* MOBILE TAB BAR (Toujours là, cachée par CSS sur desktop) */}
      <div className="admin-tabbar">
        <NavLink to="/admin" end onClick={handleNavClick} className="tab-item">
          <DashIcon />
          <span>Dash</span>
        </NavLink>
        <NavLink to="/admin/planning" onClick={handleNavClick} className="tab-item">
          <CalendarIcon />
          <span>Planning</span>
        </NavLink>
        <NavLink to="/admin/chantiers" onClick={handleNavClick} className="tab-item">
          <ShopIcon />
          <span>Chantiers</span>
        </NavLink>
        <button onClick={handleToggleSidebar} className="tab-item burger-btn">
          <div className="burger-icon-wrap">
            <span />
            <span />
            <span />
          </div>
          <span>Menu</span>
        </button>
      </div>
    </div>
  );
}
